import { useTranslation } from "react-i18next";
import {
  InspectionStatusPill,
  INSPECTION_STATUS_ORDER,
} from "../../../components/inspections/InspectionStatusPill";
import type { YardMetricsEntry } from "../../../features/truckYard/useYardMetrics";
import {
  formatAge,
  yardLinkClasses,
  yardLinkHasOutage,
  yardLinkState,
  type YardHealth,
  type YardUsageTier,
} from "./yardOperation";
import YardDownCauseLine from "./YardDownCauseLine";
import type { ThemeClasses } from "./types";

// Las claves de traducción del eje de uso son las que YA existen para la
// pantalla de control de acceso (`accessControl.tier.*`). Se reusan tal cual, y
// el comentario de USAGE_TIERS en yardHealthService.js explica por qué los
// nombres de ese eje quedaron en inglés mientras los de enlace están en
// español: para que esta tarjeta no tuviera que inventar un segundo diccionario
// que después se desincroniza del primero.
const USAGE_I18N: Record<YardUsageTier, string> = {
  online_idle: "accessControl.tier.onlineIdle",
  active: "accessControl.tier.active",
  stale_1h: "accessControl.tier.stale1h",
  stale_24h: "accessControl.tier.stale24h",
  stale_48h: "accessControl.tier.stale48h",
};

// El uso se pinta como TEXTO teñido y no como una píldora rellena, a diferencia
// del enlace. Son dos ejes distintos y la tarjeta tiene tres píldoras más abajo
// (los estados de inspección): un tercer bloque relleno los pone a competir y
// la tarjeta deja de leerse de un vistazo. Sólo los dos tramos que significan
// "acá no pasó nada en más de un día" salen del gris — es el caso de #407, el
// patio conectado del que nadie mira porque el punto está en verde.
const USAGE_TONE: Record<YardUsageTier, string> = {
  online_idle: "",
  active: "",
  stale_1h: "",
  stale_24h: "text-amber-500",
  stale_48h: "text-orange-500",
};

/**
 * La operación del patio: en qué estado está su controlador AHORA y qué pasó
 * hoy adentro.
 *
 * Reemplaza a Radio y Tel. de Caseta en las tres variantes de tarjeta. Los dos
 * eran configuración —el radio ya se ve dibujado en el mapa de la modal de
 * detalles y el teléfono se consulta una vez cada seis meses— y ocupaban los dos
 * únicos huecos de dato que tiene la tarjeta.
 */
export default function YardOperationSummary({
  health,
  metrics,
  themeClasses,
  compact = false,
}: {
  health: YardHealth | null | undefined;
  metrics: YardMetricsEntry | undefined;
  themeClasses: ThemeClasses;
  compact?: boolean;
}) {
  const { t } = useTranslation("truckYard");

  const link = yardLinkState(health);
  const palette = yardLinkClasses(link);

  // "Desde cuándo" sólo cuando hay corte. En `degradado` la pregunta del
  // operador es la misma que en `caido` ("¿desde cuándo?"), así que el backend
  // calcula `outage_started_at` para cualquier puerta caída, no sólo cuando
  // cayeron todas.
  const outageAge = yardLinkHasOutage(link)
    ? formatAge(health?.outage_duration_ms)
    : null;

  // "N de M sin conexión" con doors_monitored como denominador, NUNCA
  // doors_total: una puerta en modo `direct` no está ni conectada ni caída, y
  // meterla en el denominador convierte un "1 de 1 sin conexión" en un
  // tranquilizador "1 de 2".
  const doorsDetail =
    link === "degradado" && health
      ? t("yards.health.doorsDown", {
          down: health.doors_down,
          monitored: health.doors_monitored,
        })
      : null;

  const usage = health?.usage ?? null;
  const counts = metrics?.counts ?? null;
  const chipSize = compact ? "text-[11px] px-2 py-0.5" : "text-xs px-2.5 py-1";

  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      {/* Eje 1: el enlace con el controlador. */}
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full font-medium ${chipSize} ${palette.chip}`}
          title={
            health?.computed_at
              ? t("yards.health.computedAt", {
                  time: new Date(health.computed_at).toLocaleTimeString(),
                })
              : undefined
          }
        >
          <span className={`w-1.5 h-1.5 rounded-full ${palette.dot}`} />
          {t(`yards.health.link.${link}`)}
          {doorsDetail && <span className="font-normal">{doorsDetail}</span>}
          {outageAge && (
            <span className="font-normal">
              {t("yards.health.since", { age: outageAge })}
            </span>
          )}
        </span>

        {/* Eje 2: el uso. Un patio puede estar perfectamente conectado y no
            registrar un pase desde hace tres días — con un solo eje ese patio
            sale verde y nadie lo mira. */}
        {usage && (
          <span
            className={`inline-flex items-center gap-1 text-[11px] ${
              USAGE_TONE[usage] || themeClasses.textSecondary
            }`}
            title={
              health?.last_event_age_ms != null
                ? t("yards.health.lastPass", {
                    age: formatAge(health.last_event_age_ms),
                  })
                : undefined
            }
          >
            <i className="ri-time-line"></i>
            {t(USAGE_I18N[usage])}
          </span>
        )}
      </div>

      {/* Quién no responde: la PC del sitio o la controladora. Va en su propio
          renglón y no adentro de la píldora porque la píldora ya lleva estado,
          "N de M" y "hace X"; una frase más ahí la parte en dos líneas en la
          tarjeta compacta. No ocupa lugar cuando no hay causa. */}
      <YardDownCauseLine health={health} className={themeClasses.textSecondary} />

      {/* Eje 3: qué pasó hoy adentro del patio. */}
      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
        {metrics?.isLoading ? (
          <span className={`text-[11px] ${themeClasses.textSecondary}`}>
            {t("yards.operation.loading")}
          </span>
        ) : metrics?.isError || !counts ? (
          <span className={`text-[11px] ${themeClasses.textSecondary}`}>
            <i className="ri-error-warning-line mr-1"></i>
            {t("yards.operation.error")}
          </span>
        ) : (metrics?.total ?? 0) === 0 ? (
          // Un cero explícito, no una fila vacía: "no cargó" y "hoy no entró
          // nadie" son respuestas distintas y una fila en blanco las confunde.
          <span className={`text-[11px] ${themeClasses.textSecondary}`}>
            <i className="ri-inbox-line mr-1"></i>
            {t("yards.operation.noMovement")}
          </span>
        ) : (
          <>
            {/* El rango tiene que estar escrito. Sin esto, cuatro píldoras con
                números sueltos se leen igual de bien como "hoy" que como
                "histórico del patio", y son respuestas muy distintas. El día es
                el del PATIO (yardLocalDay), no el del navegador. */}
            <span
              className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}
              title={metrics?.day}
            >
              {t("yards.operation.today")}
            </span>
            {INSPECTION_STATUS_ORDER.map(
              (status) =>
                counts[status] > 0 && (
                  <InspectionStatusPill
                    key={status}
                    status={status}
                    count={counts[status]}
                    compact
                    title={
                      status === "complete"
                        ? t("yards.operation.completeHint")
                        : undefined
                    }
                  />
                ),
            )}
          </>
        )}
      </div>
    </div>
  );
}
